import type { ReactNode } from 'react';
import {
  Header,
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuSection as AriaMenuSection,
  MenuTrigger,
  Popover,
  Separator,
} from 'react-aria-components';
import { Button, type ButtonProps } from './Button.tsx';
import styles from './Menu.module.css';

export interface MenuButtonItem {
  /** Unique id across the whole menu (FE-036 duplicate-key lesson). */
  id: string;
  label: string;
}

export interface MenuSectionSpec {
  /** Section heading; also used to prefix item ids when needed. */
  title: string;
  items: MenuButtonItem[];
}

interface MenuButtonProps {
  label: string;
  /** One flat list of items (no sections). */
  items?: MenuButtonItem[];
  /** Sectioned items; item ids must stay unique across sections. */
  sections?: MenuSectionSpec[];
  onSelect: (id: string) => void;
  buttonProps?: Omit<ButtonProps, 'onPress'>;
  children?: ReactNode;
}

/**
 * Menu button (react-aria-components): arrow keys + Enter/Space select, Escape
 * returns focus to the trigger. Every item id must be unique across sections —
 * react-aria merges duplicate keys silently.
 */
export function MenuButton({
  label,
  items,
  sections,
  onSelect,
  buttonProps,
  children,
}: MenuButtonProps) {
  const flat = sections?.flatMap((s) => s.items) ?? items ?? [];
  return (
    <MenuTrigger>
      <Button aria-label={label} {...buttonProps}>
        {children ?? label}
      </Button>
      <Popover className={styles.popover} placement="bottom end" offset={8}>
        <AriaMenu
          className={styles.menu}
          aria-label={label}
          onAction={(key) => onSelect(String(key))}
        >
          {sections
            ? sections.map((section, i) => (
                <AriaMenuSection key={section.title}>
                  <Header className={styles.header}>{section.title}</Header>
                  {section.items.map((item) => (
                    <AriaMenuItem key={item.id} id={item.id} className={styles.item}>
                      {item.label}
                    </AriaMenuItem>
                  ))}
                  {i < sections.length - 1 && <Separator className={styles.separator} />}
                </AriaMenuSection>
              ))
            : flat.map((item) => (
                <AriaMenuItem key={item.id} id={item.id} className={styles.item}>
                  {item.label}
                </AriaMenuItem>
              ))}
        </AriaMenu>
      </Popover>
    </MenuTrigger>
  );
}
